import React, { useEffect, useState } from "react";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ApartmentOutlined,
  UserOutlined,
  AppstoreOutlined,
  CompassOutlined,
  EnvironmentOutlined,
  FileAddOutlined,
  LogoutOutlined,
  LoadingOutlined,
  ScheduleOutlined,
  ToolOutlined,
  SolutionOutlined,
  IdcardOutlined,
  CarOutlined
} from "@ant-design/icons";
import { Layout, MenuProps, Menu, Button } from "antd";

import Home from "../Pages/Home";
import User from "../Pages/Users/User";
import Brand from "../Pages/Brands/Brand";
import Partner from "../Pages/Partner/Partner";
import Location from "../Pages/Locations/Location";
import FormSection from "../Pages/Forms/FormSection";
import { useDispatch, useSelector } from "react-redux";
import { setAuthData } from "../redux/slices/authSlice";
import { useNavigate } from "react-router";
import { useValidateTokenMutation } from "../redux/services/userApi";
import { baseApi } from "../redux/services/baseApi";
import { useAppSelector } from "../redux/hooks";
import Installation from "../Pages/Booking/Installation/Installation";
import FinanceInstallation from "../Pages/Booking/FinanceInstallation/FinanceInstallation";
import FinanceSurvey from "../Pages/Booking/FinanceSurvey/FinanceSurvey";
import Booking from "../Pages/Booking/Booking/Booking";
import Survey from "../Pages/Booking/Survey/Survey";
import Complaint from "../Pages/Booking/Complaint/Complaint";
import Visit from "../Pages/Booking/Visit/Visit";
import { Link, Outlet, Route, Routes } from "react-router-dom";
import ViewBooking from "../Pages/Booking/Booking/ViewBooking";
import UploadBooking from "../Pages/Booking/Booking/UploadBooking";
import UploadBookingStatus from "../Pages/Booking/Booking/UploadBookingStatus";
import UploadExcel from "../Pages/UploadUtility/UploadExcel";
import UploadSurveyInvoiceClaim from "../Pages/Booking/Survey/UploadSurveyInvoiceClaim";
import UploadInstallationInvoiceClaim from "../Pages/Booking/Installation/UploadInstallationInvoiceClaim";
import UploadResurveyInvoiceClaim from "../Pages/Booking/Survey/UploadResurveyInvoiceClaim";
import BookingForm from "../Pages/Booking/Booking/BookingForm";
import ViewComplain from "../Pages/Booking/Complaint/ViewComplaint";
import UploadComplain from "../Pages/Booking/Complaint/UploadComplaint";
import ComplainForm from "../Pages/Booking/Complaint/ComplaintForm";
import SurveyForm from "../Pages/Booking/Survey/SurveyForm";
import VisitForm from "../Pages/Booking/Visit/VisitForm";
import ViewUser from "../Pages/Users/ViewUser";
import ViewSurvey from "../Pages/Booking/Survey/ViewSurvey";
import ViewVisit from "../Pages/Booking/Visit/ViewVisit";
import ViewInstallation from "../Pages/Booking/Installation/ViewInstallation";
import InstallationForm from "../Pages/Booking/Installation/InstallationForm";
import ViewBrand from "../Pages/Brands/ViewBrand";
import ViewBrandModel from "../Pages/BrandModels/ViewBrandModel";
import ViewDealerMapping from "../Pages/DealerMappings/ViewDealerMapping";
import ViewLocation from "../Pages/Locations/ViewLocation";
import ViewPartner from "../Pages/Partner/ViewPartner";
import { RootState } from "../redux/store";
import { isUserHasRoleTypes } from "../utils";

const { Sider, Content } = Layout;

const MENU_ITEM = {
  DASHBOARD: "Dashboard",
  BOOKING: "Bookings",
  INSATALLATION: "Installations",
  SURVEYS: "Surveys",
  COMPLAINT: "Complaints",
  VISIT: "Visits",
  BRAND: "Brands",
  DEALER: "Dealers",
  LOCATION: "Locations",
  PARTNER: "Partners",
  USER: "Users",
  FORM: "Forms",
  LOGOUT: "Logout"
};

const AppLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [hasAccessToMasterData, setHasAccessToMasterData] = useState(false);
  const user = useAppSelector<any>((store) => store.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isClient = user?.user_type === "client";

  const isFinanceController = isUserHasRoleTypes(user, ["finance_controller"]);
  const accessToken = useSelector((state: RootState) => state?.auth?.accessToken);

  const [validateToken, { isLoading: tokenValidationInProgress, isError }] =
    useValidateTokenMutation();

  useEffect(() => {
    async function checkAccessToken() {
      if (accessToken) {
        try {
          await validateToken({}).unwrap();
        } catch (error) {
          dispatch(setAuthData({ accessToken: null, client: null, uid: null, user: null }));
        }
      } else {
        navigate("/login");
      }
    }

    checkAccessToken();
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (isError) {
      navigate("/login");
    }
  }, [isError]);

  useEffect(() => {
    const userHasAccessToMasterData = user?.roles?.some((role: { role_type: string }) =>
      ["super_user"].includes(role.role_type)
    );
    setHasAccessToMasterData(userHasAccessToMasterData);
  }, [user]);

  const onClick: MenuProps["onClick"] = (e: any) => {
    navigate(e.key);
  };

  const onLogout = () => {
    dispatch(setAuthData({ accessToken: null, client: null, uid: null }));
    navigate("/login");
    dispatch(baseApi.util.resetApiState());
  };

  const items1 = [
    {
      key: "/",
      icon: <AppstoreOutlined />,
      label: MENU_ITEM.DASHBOARD
    },
    {
      key: "/bookings",
      icon: <ScheduleOutlined />,
      label: MENU_ITEM.BOOKING
    },
    {
      key: "/surveys",
      icon: <SolutionOutlined />,
      label: MENU_ITEM.SURVEYS
    },
    {
      key: "/installations",
      icon: <ToolOutlined />,
      label: MENU_ITEM.INSATALLATION
    },
    {
      key: "/complaints",
      icon: <IdcardOutlined />,
      label: MENU_ITEM.COMPLAINT
    },
    {
      key: "/visits",
      icon: <CarOutlined />,
      label: MENU_ITEM.VISIT
    },
    {
      key: "/brands",
      icon: <CompassOutlined />,
      label: MENU_ITEM.BRAND
    },
    {
      key: "/partners",
      icon: <ApartmentOutlined />,
      label: MENU_ITEM.PARTNER
    },
    {
      key: "/locations",
      icon: <EnvironmentOutlined />,
      label: MENU_ITEM.LOCATION
    },
    {
      key: "/users",
      icon: <UserOutlined />,
      label: MENU_ITEM.USER
    },
    {
      key: "/form",
      icon: <FileAddOutlined />,
      label: MENU_ITEM.FORM
    }
  ];

  const items2 = [
    {
      key: "/bookings",
      icon: <ScheduleOutlined />,
      label: MENU_ITEM.BOOKING
    },
    {
      key: "/surveys",
      icon: <SolutionOutlined />,
      label: MENU_ITEM.SURVEYS
    },
    {
      key: "/installations",
      icon: <ToolOutlined />,
      label: MENU_ITEM.INSATALLATION
    },
    {
      key: "/complaints",
      icon: <IdcardOutlined />,
      label: MENU_ITEM.COMPLAINT
    },
    {
      key: "/visits",
      icon: <CarOutlined />,
      label: MENU_ITEM.VISIT
    }
  ];

  const items3 = [
    {
      key: "/",
      icon: <AppstoreOutlined />,
      label: MENU_ITEM.DASHBOARD
    },
    {
      key: "/bookings",
      icon: <ScheduleOutlined />,
      label: MENU_ITEM.BOOKING
    },
    {
      key: "/surveys",
      icon: <SolutionOutlined />,
      label: MENU_ITEM.SURVEYS
    },
    {
      key: "/installations",
      icon: <ToolOutlined />,
      label: MENU_ITEM.INSATALLATION
    },
    {
      key: "/complaints",
      icon: <IdcardOutlined />,
      label: MENU_ITEM.COMPLAINT
    },
    {
      key: "/visits",
      icon: <CarOutlined />,
      label: MENU_ITEM.VISIT
    },
    {
      key: "/users",
      icon: <UserOutlined />,
      label: MENU_ITEM.USER
    }
  ];

  const items4 = [
    {
      key: "/",
      icon: <AppstoreOutlined />,
      label: MENU_ITEM.DASHBOARD
    },
    {
      key: "/bookings",
      icon: <ScheduleOutlined />,
      label: MENU_ITEM.BOOKING
    },
    {
      key: "/surveys",
      icon: <SolutionOutlined />,
      label: MENU_ITEM.SURVEYS
    },
    {
      key: "/installations",
      icon: <ToolOutlined />,
      label: MENU_ITEM.INSATALLATION
    },
    {
      key: "/complaints",
      icon: <IdcardOutlined />,
      label: MENU_ITEM.COMPLAINT
    },
    {
      key: "/visits",
      icon: <CarOutlined />,
      label: MENU_ITEM.VISIT
    }
  ];

  const items5 = [
    {
      key: "/",
      icon: <AppstoreOutlined />,
      label: MENU_ITEM.DASHBOARD
    },
    {
      key: "/bookings",
      icon: <ScheduleOutlined />,
      label: MENU_ITEM.BOOKING
    },
    {
      key: "/surveys",
      icon: <SolutionOutlined />,
      label: MENU_ITEM.SURVEYS
    },
    {
      key: "/installations",
      icon: <ToolOutlined />,
      label: MENU_ITEM.INSATALLATION
    },
    {
      key: "/complaints",
      icon: <IdcardOutlined />,
      label: MENU_ITEM.COMPLAINT
    },
    {
      key: "/visits",
      icon: <CarOutlined />,
      label: MENU_ITEM.VISIT
    }
  ];

  const items6 = [
    {
      key: "/",
      icon: <AppstoreOutlined />,
      label: MENU_ITEM.DASHBOARD
    },
    {
      key: "/surveys",
      icon: <SolutionOutlined />,
      label: MENU_ITEM.SURVEYS
    },
    {
      key: "/installations",
      icon: <ToolOutlined />,
      label: MENU_ITEM.INSATALLATION
    }
  ];

  let items = [];
  if (hasAccessToMasterData) {
    items = items1;
  } else if (isUserHasRoleTypes(user, ["city_manager", "zonal_manager", "mis"])) {
    items = items3;
  } else if (isUserHasRoleTypes(user, ["management"])) {
    items = items4;
  } else if (isClient) {
    items = items5;
  } else if (isUserHasRoleTypes(user, ["finance_controller"])) {
    items = items6;
  } else {
    items = items2;
  }

  return (
    <>
      {!tokenValidationInProgress && (
        <Layout style={{ height: "100vh", overflow: "auto" }}>
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            style={{
              padding: 0,
              margin: 0,
              height: "auto",
              width: "100vw",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              backgroundColor: "white",
              color: "black"
            }}
          >
            <Link to="/">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 0,
                  margin: 0,
                  height: 64,
                  alignItems: "center"
                }}
              >
                {!collapsed && <img src="/assets/logo.svg" alt="logo" width={200} height={38} />}
                {collapsed && <img src="/assets/logo-without-text.svg" alt="logo" height={40} />}
              </div>
            </Link>

            <Menu
              theme="light"
              mode="inline"
              onClick={onClick}
              defaultSelectedKeys={[MENU_ITEM.DASHBOARD]}
              items={items}
            />

            <Menu
              theme="light"
              mode="inline"
              onClick={onLogout}
              items={[
                {
                  key: MENU_ITEM.LOGOUT,
                  icon: <LogoutOutlined />,
                  label: MENU_ITEM.LOGOUT
                }
              ]}
            />
            <div
              style={{
                height: 80,
                color: "black",
                position: "absolute",
                bottom: 0
              }}
            >
              {user?.name && !collapsed && (
                <div>
                  <h4
                    style={{
                      marginLeft: 30,
                      marginTop: 34,
                      width: 100
                    }}
                  >
                    {user?.name}
                  </h4>
                </div>
              )}

              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  fontSize: 24,
                  width: 80,
                  height: 80,
                  marginLeft: collapsed ? 0 : 120,
                  color: "black",
                  position: "absolute",
                  bottom: 0
                }}
              />
            </div>
          </Sider>
          <Layout style={{ overflow: "scroll" }}>
            <Content
              style={{
                margin: "12px 16px 24px 16px",
                padding: 24,
                borderRadius: 8
              }}
            >
              {isFinanceController ? (
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/surveys">
                    <Route index element={<FinanceSurvey />} />
                    <Route path=":surveyId/update" element={<SurveyForm />} />
                    <Route path=":surveyId" element={<ViewSurvey />} />
                    <Route index element={<Survey />} />
                    <Route
                      path="upload-invoices"
                      element={
                        <UploadBookingStatus
                          heading="Upload Invoices"
                          action="import_invoices"
                          successMessage="Survey Invoices Uploaded Successfully"
                        />
                      }
                    />
                    <Route
                      path="upload-survey-invoice-claim"
                      element={
                        <UploadSurveyInvoiceClaim
                          heading="Upload Survey Invoices Claim"
                          action="import_survey_invoice_claim"
                          successMessage="Survey Invoices Uploaded Successfully"
                        />
                      }
                    />
                    <Route
                      path="upload-resurvey-invoice-claim"
                      element={
                        <UploadResurveyInvoiceClaim
                          heading="Upload Re-Survey Invoices Claim"
                          action="import_resurvey_invoice_claim"
                          successMessage="Survey Invoices Uploaded Successfully"
                        />
                      }
                    />
                    <Route
                      path="import-payment-details"
                      element={
                        <UploadBookingStatus
                          heading="Upload Payment Details"
                          action="import_payment_details"
                          successMessage="Payment Details Uploaded Successfully"
                        />
                      }
                    />
                  </Route>
                  <Route path="/installations">
                    <Route index element={<FinanceInstallation />} />
                    <Route path=":installationId" element={<ViewInstallation />} />
                    <Route path=":installationId/update" element={<InstallationForm />} />
                    <Route
                      path="upload-installation-invoice-claim"
                      element={
                        <UploadInstallationInvoiceClaim
                          heading="Upload installation Invoices Claim"
                          action="import_installation_invoice_claim"
                          successMessage="Installation Invoices Uploaded Successfully"
                        />
                      }
                    />
                  </Route>
                </Routes>
              ) : isClient ? (
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/bookings">
                    <Route index element={<Booking />} />
                    <Route path=":bookingId" element={<ViewBooking />} />
                  </Route>
                  <Route path="/surveys">
                    <Route index element={<Survey />} />
                    <Route path=":surveyId" element={<ViewSurvey />} />
                  </Route>
                  <Route path="/installations">
                    <Route index element={<Installation />} />
                    <Route path=":installationId" element={<ViewInstallation />} />
                  </Route>
                  <Route path="/complaints">
                    <Route index element={<Complaint />} />
                    <Route path="upload-complaints" element={<UploadComplain />} />
                    <Route path=":complaintId" element={<ViewComplain />} />
                  </Route>
                  <Route path="/visits">
                    <Route index element={<Visit />} />
                    <Route path=":visitId" element={<ViewVisit />} />
                  </Route>
                </Routes>
              ) : (
                <Routes>
                  <Route path="/" element={<Home />} />

                  <Route path="/bookings">
                    <Route index element={<Booking />} />
                    <Route path="upload-bookings" element={<UploadBooking />} />
                    <Route
                      path="update-bookings-status"
                      element={
                        <UploadBookingStatus
                          heading="Update Brand Booking Status"
                          action="import_booking_status"
                          successMessage="Brand Booking Status Updated Successfully"
                        />
                      }
                    />
                    <Route
                      path="transfer-bookings"
                      element={
                        <UploadBookingStatus
                          heading="Transfer Bookings"
                          action="transfer_bookings"
                          successMessage="Booking transferred successfully"
                        />
                      }
                    />
                    <Route
                      path="import-call-logs"
                      element={
                        <UploadBookingStatus
                          heading="Import Call Logs"
                          action="import_call_logs"
                          successMessage="Calllogs created Successfully"
                        />
                      }
                    />
                    <Route
                      path="update-pi-dates"
                      element={
                        <UploadBookingStatus
                          heading="Update PI Dates"
                          action="import_pi_dates"
                          successMessage="PI Dates Updated Successfully"
                        />
                      }
                    />
                    <Route path=":bookingId" element={<ViewBooking />} />
                    <Route path=":bookingId/update" element={<BookingForm />} />
                    <Route path=":bookingId/create-survey" element={<SurveyForm />} />
                  </Route>

                  <Route path="brands">
                    <Route index element={<Brand />} />
                    <Route
                      path="import-pricings"
                      element={
                        <UploadExcel
                          resource_url="brand_models"
                          heading="Import Pricings"
                          action="import_pricings"
                          successMessage="Pricings Uploaded Successfully"
                        />
                      }
                    />
                  </Route>

                  <Route path="/surveys">
                    <Route index element={<Survey />} />
                    <Route
                      path="upload-invoices"
                      element={
                        <UploadBookingStatus
                          heading="Upload Invoices"
                          action="import_invoices"
                          successMessage="Survey Invoices Uploaded Successfully"
                        />
                      }
                    />
                    <Route
                      path="upload-refund-details"
                      element={
                        <UploadBookingStatus
                          heading="Upload Refund Details"
                          action="import_refund_details"
                          successMessage="Refund Details Updated Successfully"
                        />
                      }
                    />
                    <Route
                      path="upload-survey-invoice-claim"
                      element={
                        <UploadSurveyInvoiceClaim
                          heading="Upload Survey Invoices Claim"
                          action="import_survey_invoice_claim"
                          successMessage="Survey Invoices Uploaded Successfully"
                        />
                      }
                    />
                    <Route
                      path="upload-resurvey-invoice-claim"
                      element={
                        <UploadResurveyInvoiceClaim
                          heading="Upload Re-Survey Invoices Claim"
                          action="import_resurvey_invoice_claim"
                          successMessage="Survey Invoices Uploaded Successfully"
                        />
                      }
                    />
                    <Route path=":surveyId" element={<ViewSurvey />} />
                    <Route path=":surveyId/update" element={<SurveyForm />} />
                    <Route path=":surveyId/create-installation" element={<InstallationForm />} />
                    <Route
                      path="import-payment-details"
                      element={
                        <UploadBookingStatus
                          heading="Upload Payment Details"
                          action="import_payment_details"
                          successMessage="Payment Details Uploaded Successfully"
                        />
                      }
                    />
                  </Route>

                  <Route path="/installations">
                    <Route index element={<Installation />} />
                    <Route path=":installationId" element={<ViewInstallation />} />
                    <Route path=":installationId/update" element={<InstallationForm />} />
                    <Route
                      path="upload-installation-invoice-claim"
                      element={
                        <UploadInstallationInvoiceClaim
                          heading="Upload installation Invoices Claim"
                          action="import_installation_invoice_claim"
                          successMessage="Installation Invoices Uploaded Successfully"
                        />
                      }
                    />
                  </Route>

                  <Route path="/complaints">
                    <Route index element={<Complaint />} />
                    <Route path="upload-complaints" element={<UploadComplain />} />
                    <Route path=":complaintId" element={<ViewComplain />} />
                    <Route path=":complaintId/update" element={<ComplainForm />} />
                    <Route path=":complaintId/create-visit" element={<VisitForm />} />
                  </Route>

                  <Route path="/visits">
                    <Route index element={<Visit />} />
                    <Route path=":visitId" element={<ViewVisit />} />
                    <Route path=":visitId/update" element={<VisitForm />} />
                    <Route path=":visitId/create-installation" element={<InstallationForm />} />
                  </Route>

                  <Route path="/brands">
                    <Route index element={<Brand />} />
                    <Route path=":brandId" element={<ViewBrand />} />
                  </Route>

                  <Route path="/models">
                    <Route path=":modelId" element={<ViewBrandModel />} />
                  </Route>

                  <Route path="/dealer_mappings">
                    <Route path=":dealerMappingId" element={<ViewDealerMapping />} />
                  </Route>

                  <Route path="/locations">
                    <Route index element={<Location />} />
                    <Route path=":locationId" element={<ViewLocation />} />
                  </Route>

                  <Route path="/partners">
                    <Route index element={<Partner />} />
                    <Route path=":partnerId" element={<ViewPartner />} />
                  </Route>

                  <Route path="/form" element={<FormSection />} />

                  <Route path="/users">
                    <Route index element={<User />} />
                    <Route path=":userId" element={<ViewUser />} />
                  </Route>
                </Routes>
              )}
            </Content>
          </Layout>
        </Layout>
      )}
      {tokenValidationInProgress && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}
      <Outlet />
    </>
  );
};

export default AppLayout;
